import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { TabView, TabPanel } from "primereact/tabview";
import { Tag } from "primereact/tag";
import { Badge } from "primereact/badge";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { Divider } from "primereact/divider";
import { ProgressBar } from "primereact/progressbar";
import { Timeline } from "primereact/timeline";
import { ScrollPanel } from "primereact/scrollpanel";

import Layout from "../../component/layout";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { useFacilityStore } from "../../../../app/z-store/store";

const StockAndSpareManagement = () => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Stock Modal State
  const [showStockModal, setShowStockModal] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [stockFormData, setStockFormData] = useState({
    name: "",
    description: "",
    skuDetails: "",
    quantityInStock: 0,
    reOrderLevel: 0,
  });

  // Increment/Decrement Modal States
  const [showIncrementModal, setShowIncrementModal] = useState(false);
  const [showDecrementModal, setShowDecrementModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [stockHistory, setStockHistory] = useState([]);

  const [incrementData, setIncrementData] = useState({
    stockId: "",
    name: "",
    currentQuantity: 0,
    quantity: 0,
    description: "",
  });

  const [decrementData, setDecrementData] = useState({
    stockId: "",
    name: "",
    currentQuantity: 0,
    quantity: 0,
    description: "",
  });

  // Requisition Modal State
  const [showRequisitionModal, setShowRequisitionModal] = useState(false);
  const [requisitionData, setRequisitionData] = useState({
    stockId: "",
    currentQuantity: 0,
    issueQuantity: 0,
    issueDescription: "",
  });

  const [requisitionedStocks, setRequisitionedStocks] = useState([]);

  // Statistics State
  const [stockStats, setStockStats] = useState({
    totalItems: 0,
    lowStockItems: 0,
    averageStock: 0,
    totalValue: 0,
  });

  const toast = useRef(null);
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  useEffect(() => {
    if (selectedFacilityId) {
      fetchStocks();
      fetchRequisitionedStocks();
    }
  }, [selectedFacilityId]);

  const showToast = (severity, summary, detail) => {
    toast.current.show({ severity, summary, detail });
  };

  const calculateStats = (stockData) => {
    const totalItems = stockData.length;
    const lowStockItems = stockData.filter(
      (stock) => stock.quantityInStock <= stock.reOrderLevel,
    ).length;
    const averageStock =
      totalItems > 0
        ? Math.round(
            stockData.reduce((sum, stock) => sum + stock.quantityInStock, 0) /
              totalItems,
          )
        : 0;

    setStockStats({
      totalItems,
      lowStockItems,
      averageStock,
      totalValue: stockData.reduce(
        (sum, stock) => sum + stock.quantityInStock,
        0,
      ),
    });
  };

  const fetchStocks = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `/api/stockandspare/${selectedFacilityId}`,
        "GET",
        null,
      );

      if (response.success) {
        const stockData = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data.stocks)
            ? response.data.stocks
            : [];
        setStocks(stockData);
        calculateStats(stockData);
        setError(null);
      } else {
        setError(response.error);
        showToast("error", "Error", response.error);
      }
    } catch (err) {
      setError("Failed to fetch stocks");
      showToast("error", "Error", err.message || "Failed to fetch stocks");
    } finally {
      setLoading(false);
    }
  };

  const fetchStockHistory = async (stockId) => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `/api/stockandspare/${selectedFacilityId}/${stockId}/history`,
        "GET",
        null,
      );

      if (response.success) {
        setStockHistory(response.data.history || []);
      } else {
        showToast(
          "error",
          "Error",
          response.error || "Failed to fetch stock history",
        );
      }
    } catch (err) {
      showToast(
        "error",
        "Error",
        err.message || "Failed to fetch stock history",
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchRequisitionedStocks = async () => {
    try {
      const response = await makeRequest2(
        `/api/stockandspare/${selectedFacilityId}/requisition`,
        "GET",
        null,
      );

      if (response.success && Array.isArray(response.data.data)) {
        setRequisitionedStocks(response.data.data);
      } else {
        setRequisitionedStocks([]);
      }
    } catch (err) {
      setRequisitionedStocks([]);
      console.error("Failed to fetch requisitioned stocks:", err);
    }
  };

  const filteredStocks = Array.isArray(stocks)
    ? stocks.filter((stock) =>
        Object.values(stock).some((value) =>
          value?.toString().toLowerCase().includes(searchKeyword.toLowerCase()),
        ),
      )
    : [];

  // Stock handlers
  const handleStockInputChange = (e) => {
    setStockFormData({
      ...stockFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleStockModalOpen = (stock = null) => {
    if (stock) {
      setSelectedStock(stock);
      setStockFormData({
        name: stock.name || "",
        description: stock.description || "",
        skuDetails: stock.skuDetails || "",
        quantityInStock: stock.quantityInStock || 0,
        reOrderLevel: stock.reOrderLevel || 0,
      });
    } else {
      setSelectedStock(null);
      setStockFormData({
        name: "",
        description: "",
        skuDetails: "",
        quantityInStock: 0,
        reOrderLevel: 0,
      });
    }
    setShowStockModal(true);
  };

  const handleStockSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let response;
      if (selectedStock) {
        const updatePayload = {
          ...stockFormData,
          metadataDescription: `Stock details updated via management interface`,
        };
        response = await makeRequest2(
          `/api/stockandspare/${selectedFacilityId}/${selectedStock._id}`,
          "PUT",
          updatePayload,
        );
      } else {
        response = await makeRequest2(
          `/api/stockandspare/${selectedFacilityId}`,
          "POST",
          stockFormData,
        );
      }

      if (response.success) {
        showToast(
          "success",
          "Success",
          selectedStock
            ? "Stock updated successfully"
            : "Stock added successfully",
        );
        fetchStocks();
        setShowStockModal(false);
      } else {
        showToast("error", "Error", response.error);
      }
    } catch (err) {
      showToast("error", "Error", "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Increment/Decrement handlers
  const handleIncrementModalOpen = (stock) => {
    setIncrementData({
      stockId: stock._id,
      name: stock.name,
      currentQuantity: stock.quantityInStock,
      quantity: 0,
      description: "",
    });
    setShowIncrementModal(true);
  };

  const handleDecrementModalOpen = (stock) => {
    setDecrementData({
      stockId: stock._id,
      name: stock.name,
      currentQuantity: stock.quantityInStock,
      quantity: 0,
      description: "",
    });
    setShowDecrementModal(true);
  };

  const handleHistoryModalOpen = async (stock) => {
    setSelectedStock(stock);
    setShowHistoryModal(true);
    await fetchStockHistory(stock._id);
  };

  const handleIncrementSubmit = async (e) => {
    e.preventDefault();
    if (!incrementData.quantity || incrementData.quantity <= 0) {
      showToast("error", "Error", "Please enter a valid positive quantity");
      return;
    }

    if (!incrementData.description.trim()) {
      showToast(
        "error",
        "Error",
        "Please provide a description for this increment",
      );
      return;
    }

    try {
      setLoading(true);
      const response = await makeRequest2(
        `/api/stockandspare/${selectedFacilityId}/${incrementData.stockId}/increment`,
        "POST",
        {
          quantity: incrementData.quantity,
          description: incrementData.description,
        },
      );

      if (response.success) {
        showToast(
          "success",
          "Success",
          `Successfully added ${incrementData.quantity} units to stock`,
        );
        setShowIncrementModal(false);
        fetchStocks();
      } else {
        showToast(
          "error",
          "Error",
          response.error || "Failed to increment stock",
        );
      }
    } catch (err) {
      showToast("error", "Error", err.message || "Failed to increment stock");
    } finally {
      setLoading(false);
    }
  };

  const handleDecrementSubmit = async (e) => {
    e.preventDefault();
    if (!decrementData.quantity || decrementData.quantity <= 0) {
      showToast("error", "Error", "Please enter a valid positive quantity");
      return;
    }

    if (decrementData.quantity > decrementData.currentQuantity) {
      showToast(
        "error",
        "Error",
        "Cannot reduce more than current stock quantity",
      );
      return;
    }

    if (!decrementData.description.trim()) {
      showToast(
        "error",
        "Error",
        "Please provide a description for this reduction",
      );
      return;
    }

    try {
      setLoading(true);
      const response = await makeRequest2(
        `/api/stockandspare/${selectedFacilityId}/${decrementData.stockId}/decrement`,
        "POST",
        {
          quantity: decrementData.quantity,
          description: decrementData.description,
        },
      );

      if (response.success) {
        showToast(
          "success",
          "Success",
          `Successfully reduced ${decrementData.quantity} units from stock`,
        );
        setShowDecrementModal(false);
        fetchStocks();
      } else {
        showToast("error", "Error", response.error || "Failed to reduce stock");
      }
    } catch (err) {
      showToast("error", "Error", err.message || "Failed to reduce stock");
    } finally {
      setLoading(false);
    }
  };

  // Requisition handlers
  const handleRequisitionModalOpen = (stock) => {
    setRequisitionData({
      stockId: stock._id,
      currentQuantity: stock.quantityInStock,
      issueQuantity: 0,
      issueDescription: "",
    });
    setShowRequisitionModal(true);
  };

  const handleRequisitionSubmit = async (e) => {
    e.preventDefault();
    if (!requisitionData.issueQuantity || requisitionData.issueQuantity <= 0) {
      showToast("error", "Error", "Please enter a valid quantity");
      return;
    }

    if (requisitionData.issueQuantity > requisitionData.currentQuantity) {
      showToast("error", "Error", "Issue quantity cannot exceed current stock");
      return;
    }

    if (!requisitionData.issueDescription) {
      showToast(
        "error",
        "Error",
        "Please provide a description for the requisition",
      );
      return;
    }

    try {
      setLoading(true);
      // Use the new decrement endpoint with description
      const decrementResponse = await makeRequest2(
        `/api/stockandspare/${selectedFacilityId}/${requisitionData.stockId}/decrement`,
        "POST",
        {
          quantity: requisitionData.issueQuantity,
          description: `Stock requisition: ${requisitionData.issueDescription}`,
        },
      );

      if (!decrementResponse.success) {
        throw new Error(
          decrementResponse.error || "Failed to update stock quantity",
        );
      }

      // Add stock requisition
      const requisitionResponse = await makeRequest2(
        `/api/stockandspare/${selectedFacilityId}/requisition`,
        "POST",
        {
          stockId: requisitionData.stockId,
          quantity: requisitionData.issueQuantity,
          description: requisitionData.issueDescription,
        },
      );

      if (!requisitionResponse.success) {
        throw new Error(
          requisitionResponse.error || "Failed to create requisition",
        );
      }

      showToast(
        "success",
        "Success",
        "Stock issued and requisition created successfully",
      );
      setShowRequisitionModal(false);
      fetchStocks();
      fetchRequisitionedStocks();
    } catch (err) {
      showToast(
        "error",
        "Error",
        err.message || "Failed to process stock requisition",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStock = (stock) => {
    confirmDialog({
      message: `Are you sure you want to delete stock item "${stock.name}"?`,
      header: "Delete Confirmation",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          const response = await makeRequest2(
            `/api/stockandspare/${selectedFacilityId}/${stock._id}`,
            "DELETE",
            null,
          );

          if (response.success) {
            showToast("success", "Success", "Stock deleted successfully");
            fetchStocks();
          } else {
            showToast("error", "Error", response.error);
          }
        } catch (err) {
          showToast("error", "Error", "Failed to delete stock");
        }
      },
    });
  };

  // Column templates
  const stockLevelTemplate = (rowData) => {
    const isLowStock = rowData.quantityInStock <= rowData.reOrderLevel;
    return (
      <div className="d-flex align-items-center gap-2">
        <Badge
          value={rowData.quantityInStock}
          severity={isLowStock ? "danger" : "success"}
          size="large"
        />
        {isLowStock && (
          <Tag
            value="Low Stock"
            severity="danger"
            className="font-semibold p-tag-sm"
          />
        )}
      </div>
    );
  };

  const actionTemplate = (rowData) => {
    return (
      <div className="d-flex gap-1 flex-wrap">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-outlined p-button-info"
          size="small"
          onClick={() => handleStockModalOpen(rowData)}
          tooltip="Edit Stock"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-plus"
          className="p-button-rounded p-button-outlined p-button-success"
          size="small"
          onClick={() => handleIncrementModalOpen(rowData)}
          tooltip="Add Stock"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-minus"
          className="p-button-rounded p-button-outlined p-button-warning"
          size="small"
          onClick={() => handleDecrementModalOpen(rowData)}
          tooltip="Reduce Stock"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-history"
          className="p-button-rounded p-button-outlined p-button-secondary"
          size="small"
          onClick={() => handleHistoryModalOpen(rowData)}
          tooltip="View History"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-outlined p-button-danger"
          size="small"
          onClick={() => handleDeleteStock(rowData)}
          tooltip="Delete Stock"
          tooltipOptions={{ position: "top" }}
        />
      </div>
    );
  };

  const requisitionActionTemplate = (rowData) => {
    return (
      <Button
        icon="pi pi-send"
        label="Issue Stock"
        className="p-button-primary p-button-sm"
        onClick={() => handleRequisitionModalOpen(rowData)}
        disabled={rowData.quantityInStock <= 0}
      />
    );
  };

  const renderHeader = () => {
    return (
      <div className="d-flex justify-content-between align-items-center p-3 bg-light border-bottom">
        <div className="d-flex align-items-center gap-3">
          <i className="pi pi-search text-muted"></i>
          <InputText
            type="text"
            placeholder="Search stocks..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            className="p-inputtext-sm"
            style={{ width: "300px" }}
          />
        </div>
        <Button
          label="Add New Stock"
          icon="pi pi-plus"
          onClick={() => handleStockModalOpen()}
          className="p-button-primary"
        />
      </div>
    );
  };

  const renderRequisitionHeader = () => {
    return (
      <div className="d-flex justify-content-between align-items-center p-3 bg-light border-bottom">
        <div className="d-flex align-items-center gap-3">
          <i className="pi pi-search text-muted"></i>
          <InputText
            type="text"
            placeholder="Search stocks..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            className="p-inputtext-sm"
            style={{ width: "300px" }}
          />
        </div>
      </div>
    );
  };

  // Timeline template for history
  const timelineTemplate = (item) => {
    const getChangeIcon = (changeType) => {
      switch (changeType) {
        case "initial":
          return "pi pi-plus-circle";
        case "increment":
          return "pi pi-arrow-up";
        case "decrement":
          return "pi pi-arrow-down";
        case "edit":
          return "pi pi-pencil";
        default:
          return "pi pi-circle";
      }
    };

    const getChangeSeverity = (changeType) => {
      switch (changeType) {
        case "initial":
          return "info";
        case "increment":
          return "success";
        case "decrement":
          return "warning";
        case "edit":
          return "primary";
        default:
          return "secondary";
      }
    };

    return (
      <div className="timeline-item-content">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <Tag
            value={
              item.changeType.charAt(0).toUpperCase() + item.changeType.slice(1)
            }
            severity={getChangeSeverity(item.changeType)}
            icon={getChangeIcon(item.changeType)}
          />
          <small className="text-muted">
            {new Date(item.timestamp).toLocaleString()}
          </small>
        </div>
        <p className="mb-2">{item.description}</p>
        <div className="d-flex gap-3 text-sm">
          <span className="text-muted">
            <strong>From:</strong> {item.previousQuantity} units
          </span>
          <span className="text-muted">
            <strong>To:</strong> {item.newQuantity} units
          </span>
          <span
            className={`text-${item.quantityChanged >= 0 ? "success" : "warning"}`}
          >
            <strong>Change:</strong> {item.quantityChanged > 0 ? "+" : ""}
            {item.quantityChanged} units
          </span>
        </div>
      </div>
    );
  };

  // Statistics cards component
  const StatisticsCards = () => {
    if (loading) {
      return (
        <div className="row mb-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="col-md-3 col-sm-6 mb-3">
              <div className="card h-100">
                <div className="card-body">
                  <Skeleton height="60px" />
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="row mb-4">
        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card bg-primary text-white h-100 shadow-sm statistics-card">
            <div className="card-body d-flex align-items-center">
              <div className="flex-grow-1">
                <h2 className="mb-0 text-white">{stockStats.totalItems}</h2>
                <p className="mb-0 text-white-50">Total Stock Items</p>
              </div>
              <div className="ms-3">
                <i
                  className="pi pi-box"
                  style={{ fontSize: "2rem", opacity: 0.7 }}
                ></i>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card bg-danger text-white h-100 shadow-sm statistics-card">
            <div className="card-body d-flex align-items-center">
              <div className="flex-grow-1">
                <h2 className="mb-0 text-white">{stockStats.lowStockItems}</h2>
                <p className="mb-0 text-white-50">Low Stock Alerts</p>
              </div>
              <div className="ms-3">
                <i
                  className="pi pi-exclamation-triangle"
                  style={{ fontSize: "2rem", opacity: 0.7 }}
                ></i>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card bg-success text-white h-100 shadow-sm statistics-card">
            <div className="card-body d-flex align-items-center">
              <div className="flex-grow-1">
                <h2 className="mb-0 text-white">{stockStats.averageStock}</h2>
                <p className="mb-0 text-white-50">Average Stock Level</p>
              </div>
              <div className="ms-3">
                <i
                  className="pi pi-chart-line"
                  style={{ fontSize: "2rem", opacity: 0.7 }}
                ></i>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card bg-warning text-white h-100 shadow-sm statistics-card">
            <div className="card-body d-flex align-items-center">
              <div className="flex-grow-1">
                <h2 className="mb-0 text-white">
                  {requisitionedStocks.length}
                </h2>
                <p className="mb-0 text-white-50">Issued Items</p>
              </div>
              <div className="ms-3">
                <i
                  className="pi pi-send"
                  style={{ fontSize: "2rem", opacity: 0.7 }}
                ></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <style jsx>{`
        .p-datatable .p-datatable-tbody > tr > td {
          padding: 0.75rem;
          border-color: #e9ecef;
        }

        .p-datatable .p-datatable-thead > tr > th {
          padding: 1rem 0.75rem;
          background: #f8f9fa;
          border-color: #dee2e6;
          font-weight: 600;
          color: #495057;
        }

        .p-tabview .p-tabview-nav li .p-tabview-nav-link {
          padding: 1rem 1.5rem;
          font-weight: 500;
        }

        .statistics-card {
          transition: transform 0.2s ease-in-out;
        }

        .statistics-card:hover {
          transform: translateY(-2px);
        }

        .p-dialog .p-dialog-header {
          background: #f8f9fa;
          border-bottom: 1px solid #dee2e6;
        }

        .p-dialog .p-dialog-content {
          padding: 2rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-label {
          font-weight: 500;
          color: #495057;
          margin-bottom: 0.5rem;
          display: block;
        }

        .p-card {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          border-radius: 8px;
        }

        .p-card .p-card-body {
          padding: 1.5rem;
        }

        .timeline-item-content {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 1rem;
          border-left: 4px solid #dee2e6;
        }

        .stock-info-alert {
          background: #e3f2fd;
          border: 1px solid #bbdefb;
          border-radius: 6px;
          padding: 1rem;
          margin-bottom: 1rem;
        }

        .text-sm {
          font-size: 0.875rem;
        }

        .p-tag-sm {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
        }
      `}</style>

      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to={"/facility/"} className="text-decoration-none">
                      <i className="pi pi-home me-1"></i>
                      Dashboard
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Stock & Spare Parts Management
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="container-fluid">
        <StatisticsCards />

        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-body p-0">
                <TabView
                  activeIndex={activeTabIndex}
                  onTabChange={(e) => setActiveTabIndex(e.index)}
                  className="custom-tabview"
                >
                  {/* Available Stocks Tab */}
                  <TabPanel
                    header={`Available Stocks ${stockStats.totalItems > 0 ? `(${stockStats.totalItems})` : ""}`}
                    leftIcon="pi pi-box me-2"
                  >
                    <div className="p-3">
                      <DataTable
                        value={filteredStocks}
                        emptyMessage="No stocks found."
                        sortMode="multiple"
                        paginator
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        stripedRows
                        loading={loading}
                        header={renderHeader}
                        className="p-datatable-striped"
                        responsiveLayout="scroll"
                        breakpoint="960px"
                      >
                        <Column
                          field="name"
                          header="Product Name"
                          sortable
                          className="font-semibold"
                        />
                        <Column
                          field="description"
                          header="Description"
                          sortable
                        />
                        <Column
                          field="skuDetails"
                          header="SKU Details"
                          sortable
                        />
                        <Column
                          field="quantityInStock"
                          header="Stock Level"
                          body={stockLevelTemplate}
                          sortable
                        />
                        <Column
                          field="reOrderLevel"
                          header="Reorder Level"
                          sortable
                          body={(rowData) => (
                            <span className="text-muted">
                              {rowData.reOrderLevel} units
                            </span>
                          )}
                        />
                        <Column
                          body={actionTemplate}
                          header="Actions"
                          style={{ minWidth: "280px" }}
                        />
                      </DataTable>
                    </div>
                  </TabPanel>

                  {/* Stock Requisition Tab */}
                  <TabPanel
                    header="Stock Requisition"
                    leftIcon="pi pi-clipboard me-2"
                  >
                    <div className="p-3">
                      <DataTable
                        value={filteredStocks}
                        emptyMessage="No stocks available for requisition."
                        sortMode="multiple"
                        paginator
                        rows={10}
                        stripedRows
                        loading={loading}
                        header={renderRequisitionHeader}
                        className="p-datatable-striped"
                        responsiveLayout="scroll"
                      >
                        <Column
                          field="name"
                          header="Product Name"
                          sortable
                          className="font-semibold"
                        />
                        <Column
                          field="description"
                          header="Description"
                          sortable
                        />
                        <Column
                          field="skuDetails"
                          header="SKU Details"
                          sortable
                        />
                        <Column
                          field="quantityInStock"
                          header="Available Stock"
                          body={stockLevelTemplate}
                          sortable
                        />
                        <Column
                          field="reOrderLevel"
                          header="Reorder Level"
                          sortable
                          body={(rowData) => (
                            <span className="text-muted">
                              {rowData.reOrderLevel} units
                            </span>
                          )}
                        />
                        <Column
                          body={requisitionActionTemplate}
                          header="Actions"
                        />
                      </DataTable>
                    </div>
                  </TabPanel>

                  {/* Issued Stock Tab */}
                  <TabPanel
                    header={`Issued Stock ${requisitionedStocks.length > 0 ? `(${requisitionedStocks.length})` : ""}`}
                    leftIcon="pi pi-send me-2"
                  >
                    <div className="p-3">
                      {requisitionedStocks.length > 0 ? (
                        <DataTable
                          value={requisitionedStocks}
                          emptyMessage="No issued stocks found."
                          sortMode="multiple"
                          paginator
                          rows={10}
                          stripedRows
                          loading={loading}
                          className="p-datatable-striped"
                          responsiveLayout="scroll"
                        >
                          <Column
                            field="stock.name"
                            header="Product Name"
                            sortable
                            className="font-semibold"
                          />
                          <Column
                            field="stock.skuDetails"
                            header="SKU Details"
                            sortable
                          />
                          <Column
                            field="quantity"
                            header="Quantity Issued"
                            sortable
                            body={(rowData) => (
                              <Badge
                                value={rowData.quantity}
                                severity="warning"
                                size="large"
                              />
                            )}
                          />
                          <Column
                            field="description"
                            header="Purpose"
                            sortable
                          />
                          <Column
                            field="createdAt"
                            header="Issue Date"
                            sortable
                            body={(rowData) => (
                              <span className="text-muted">
                                {new Date(rowData.createdAt).toLocaleString()}
                              </span>
                            )}
                          />
                        </DataTable>
                      ) : (
                        <div className="text-center py-5">
                          <i
                            className="pi pi-inbox text-muted"
                            style={{ fontSize: "3rem" }}
                          ></i>
                          <h4 className="mt-3 text-muted">No Issued Stock</h4>
                          <p className="text-muted">
                            No stock requisitions have been processed yet.
                          </p>
                        </div>
                      )}
                    </div>
                  </TabPanel>

                  {/* Statistics Tab */}
                  <TabPanel header="Statistics" leftIcon="pi pi-chart-bar me-2">
                    <div className="p-4">
                      <div className="row">
                        {/* Stock Overview */}
                        <div className="col-md-6 col-lg-4 mb-4">
                          <Card className="h-100 statistics-card">
                            <h5 className="card-title d-flex align-items-center mb-3">
                              <i className="pi pi-box text-primary me-2"></i>
                              Stock Overview
                            </h5>
                            <div className="row text-center">
                              <div className="col-6">
                                <div className="border-end">
                                  <h3 className="text-primary mb-1">
                                    {stockStats.totalItems}
                                  </h3>
                                  <small className="text-muted">
                                    Total Items
                                  </small>
                                </div>
                              </div>
                              <div className="col-6">
                                <h3 className="text-success mb-1">
                                  {stockStats.totalValue}
                                </h3>
                                <small className="text-muted">
                                  Total Units
                                </small>
                              </div>
                            </div>
                          </Card>
                        </div>

                        {/* Stock Levels */}
                        <div className="col-md-6 col-lg-4 mb-4">
                          <Card className="h-100 statistics-card">
                            <h5 className="card-title d-flex align-items-center mb-3">
                              <i className="pi pi-exclamation-triangle text-warning me-2"></i>
                              Stock Levels
                            </h5>
                            <div className="row text-center">
                              <div className="col-6">
                                <div className="border-end">
                                  <h4 className="text-danger mb-1">
                                    {stockStats.lowStockItems}
                                  </h4>
                                  <small className="text-muted">
                                    Low Stock
                                  </small>
                                </div>
                              </div>
                              <div className="col-6">
                                <h4 className="text-success mb-1">
                                  {stockStats.totalItems -
                                    stockStats.lowStockItems}
                                </h4>
                                <small className="text-muted">
                                  Normal Stock
                                </small>
                              </div>
                            </div>
                            <div className="mt-3">
                              <ProgressBar
                                value={
                                  stockStats.totalItems > 0
                                    ? ((stockStats.totalItems -
                                        stockStats.lowStockItems) /
                                        stockStats.totalItems) *
                                      100
                                    : 0
                                }
                                className="mb-2"
                                style={{ height: "8px" }}
                              />
                              <small className="text-muted">
                                {stockStats.totalItems > 0
                                  ? `${Math.round(((stockStats.totalItems - stockStats.lowStockItems) / stockStats.totalItems) * 100)}% Healthy Stock Levels`
                                  : "No data"}
                              </small>
                            </div>
                          </Card>
                        </div>

                        {/* Quick Actions */}
                        <div className="col-md-6 col-lg-4 mb-4">
                          <Card className="h-100 statistics-card">
                            <h5 className="card-title d-flex align-items-center mb-3">
                              <i className="pi pi-cog text-dark me-2"></i>
                              Quick Actions
                            </h5>
                            <div className="d-grid gap-2">
                              <Button
                                label="Add New Stock"
                                icon="pi pi-plus"
                                onClick={() => handleStockModalOpen()}
                                className="p-button-primary"
                              />
                              <Button
                                label="Export Stock Report"
                                icon="pi pi-download"
                                className="p-button-outlined p-button-secondary"
                                onClick={() =>
                                  showToast(
                                    "info",
                                    "Export",
                                    "Export functionality coming soon",
                                  )
                                }
                              />
                              <Button
                                label="Stock Audit"
                                icon="pi pi-list"
                                className="p-button-outlined p-button-secondary"
                                onClick={() =>
                                  showToast(
                                    "info",
                                    "Audit",
                                    "Stock audit functionality coming soon",
                                  )
                                }
                              />
                            </div>
                          </Card>
                        </div>
                      </div>
                    </div>
                  </TabPanel>
                </TabView>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stock Add/Edit Modal */}
      <Dialog
        header={
          <div className="d-flex align-items-center">
            <i
              className={`pi ${selectedStock ? "pi-pencil" : "pi-plus"} me-2`}
            ></i>
            {selectedStock ? "Edit Stock Item" : "Add New Stock Item"}
          </div>
        }
        visible={showStockModal}
        onHide={() => setShowStockModal(false)}
        style={{ width: "600px" }}
        breakpoints={{ "960px": "75vw", "641px": "95vw" }}
        modal
        className="p-fluid"
      >
        <form onSubmit={handleStockSubmit}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-tag me-1"></i>
                  Product Name
                </label>
                <InputText
                  name="name"
                  value={stockFormData.name}
                  onChange={handleStockInputChange}
                  required
                  placeholder="Enter product name"
                />
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-id-card me-1"></i>
                  SKU Details
                </label>
                <InputText
                  name="skuDetails"
                  value={stockFormData.skuDetails}
                  onChange={handleStockInputChange}
                  required
                  placeholder="Enter SKU details"
                />
              </div>
            </div>

            <div className="col-12 mb-3">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-file-text me-1"></i>
                  Description
                </label>
                <InputTextarea
                  name="description"
                  value={stockFormData.description}
                  onChange={handleStockInputChange}
                  placeholder="Enter product description"
                  rows={3}
                />
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-database me-1"></i>
                  Quantity in Stock
                </label>
                <InputNumber
                  name="quantityInStock"
                  value={stockFormData.quantityInStock}
                  onValueChange={(e) =>
                    setStockFormData({
                      ...stockFormData,
                      quantityInStock: e.value || 0,
                    })
                  }
                  min={0}
                  placeholder="0"
                  disabled={selectedStock !== null}
                />
                {selectedStock && (
                  <small className="text-muted">
                    Use increment/decrement buttons to change quantity
                  </small>
                )}
              </div>
            </div>

            <div className="col-md-6 mb-4">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-exclamation-triangle me-1"></i>
                  Reorder Level
                </label>
                <InputNumber
                  name="reOrderLevel"
                  value={stockFormData.reOrderLevel}
                  onValueChange={(e) =>
                    setStockFormData({
                      ...stockFormData,
                      reOrderLevel: e.value || 0,
                    })
                  }
                  min={0}
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          <Divider />

          <div className="d-flex justify-content-end gap-2 pt-3">
            <Button
              type="button"
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setShowStockModal(false)}
              className="p-button-outlined p-button-secondary"
            />
            <Button
              type="submit"
              label={selectedStock ? "Update Stock" : "Add Stock"}
              icon={selectedStock ? "pi pi-check" : "pi pi-plus"}
              loading={loading}
              className="p-button-primary"
            />
          </div>
        </form>
      </Dialog>

      {/* Increment Stock Modal */}
      <Dialog
        header={
          <div className="d-flex align-items-center">
            <i className="pi pi-arrow-up me-2"></i>
            Add Stock -{" "}
            <span className="text-success">{incrementData.name}</span>
          </div>
        }
        visible={showIncrementModal}
        onHide={() => setShowIncrementModal(false)}
        style={{ width: "500px" }}
        breakpoints={{ "960px": "75vw", "641px": "95vw" }}
        modal
        className="p-fluid"
      >
        <div className="stock-info-alert">
          <div className="d-flex align-items-center">
            <i className="pi pi-info-circle me-2"></i>
            <div>
              <small>
                Adding stock to: <strong>{incrementData.name}</strong>
              </small>
              <br />
              <small className="text-muted">
                Current Stock: {incrementData.currentQuantity} units
              </small>
            </div>
          </div>
        </div>

        <form onSubmit={handleIncrementSubmit}>
          <div className="row">
            <div className="col-12 mb-3">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-plus me-1"></i>
                  Quantity to Add
                </label>
                <InputNumber
                  value={incrementData.quantity}
                  onValueChange={(e) =>
                    setIncrementData({
                      ...incrementData,
                      quantity: e.value || 0,
                    })
                  }
                  min={1}
                  placeholder="Enter quantity to add"
                  required
                />
              </div>
            </div>

            <div className="col-12 mb-4">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-comment me-1"></i>
                  Description
                </label>
                <InputTextarea
                  value={incrementData.description}
                  onChange={(e) =>
                    setIncrementData({
                      ...incrementData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Reason for stock increment (e.g., New shipment received)"
                  rows={3}
                  required
                />
              </div>
            </div>
          </div>

          <Divider />

          <div className="d-flex justify-content-end gap-2 pt-3">
            <Button
              type="button"
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setShowIncrementModal(false)}
              className="p-button-outlined p-button-secondary"
            />
            <Button
              type="submit"
              label="Add Stock"
              icon="pi pi-plus"
              loading={loading}
              className="p-button-success"
            />
          </div>
        </form>
      </Dialog>

      {/* Decrement Stock Modal */}
      <Dialog
        header={
          <div className="d-flex align-items-center">
            <i className="pi pi-arrow-down me-2"></i>
            Reduce Stock -{" "}
            <span className="text-warning">{decrementData.name}</span>
          </div>
        }
        visible={showDecrementModal}
        onHide={() => setShowDecrementModal(false)}
        style={{ width: "500px" }}
        breakpoints={{ "960px": "75vw", "641px": "95vw" }}
        modal
        className="p-fluid"
      >
        <div className="alert alert-warning d-flex align-items-center mb-3">
          <i className="pi pi-exclamation-triangle me-2"></i>
          <div>
            <small>
              Reducing stock from: <strong>{decrementData.name}</strong>
            </small>
            <br />
            <small className="text-muted">
              Available Stock: {decrementData.currentQuantity} units
            </small>
          </div>
        </div>

        <form onSubmit={handleDecrementSubmit}>
          <div className="row">
            <div className="col-12 mb-3">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-minus me-1"></i>
                  Quantity to Reduce
                </label>
                <InputNumber
                  value={decrementData.quantity}
                  onValueChange={(e) =>
                    setDecrementData({
                      ...decrementData,
                      quantity: e.value || 0,
                    })
                  }
                  min={1}
                  max={decrementData.currentQuantity}
                  placeholder="Enter quantity to reduce"
                  required
                />
              </div>
            </div>

            <div className="col-12 mb-4">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-comment me-1"></i>
                  Description
                </label>
                <InputTextarea
                  value={decrementData.description}
                  onChange={(e) =>
                    setDecrementData({
                      ...decrementData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Reason for stock reduction (e.g., Used for maintenance)"
                  rows={3}
                  required
                />
              </div>
            </div>
          </div>

          <Divider />

          <div className="d-flex justify-content-end gap-2 pt-3">
            <Button
              type="button"
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setShowDecrementModal(false)}
              className="p-button-outlined p-button-secondary"
            />
            <Button
              type="submit"
              label="Reduce Stock"
              icon="pi pi-minus"
              loading={loading}
              className="p-button-warning"
            />
          </div>
        </form>
      </Dialog>

      {/* Stock History Modal */}
      <Dialog
        header={
          <div className="d-flex align-items-center">
            <i className="pi pi-history me-2"></i>
            Stock History -{" "}
            <span className="text-primary">{selectedStock?.name}</span>
          </div>
        }
        visible={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        style={{ width: "700px" }}
        breakpoints={{ "960px": "75vw", "641px": "95vw" }}
        modal
      >
        <div className="mb-3">
          <div className="alert alert-info d-flex align-items-center">
            <i className="pi pi-info-circle me-2"></i>
            <div>
              <small>
                Viewing history for: <strong>{selectedStock?.name}</strong>
              </small>
              <br />
              <small className="text-muted">
                Current Stock: {selectedStock?.quantityInStock} units
              </small>
            </div>
          </div>
        </div>

        <ScrollPanel style={{ width: "100%", height: "400px" }}>
          {stockHistory.length > 0 ? (
            <Timeline
              value={stockHistory}
              align="left"
              className="customized-timeline"
              marker={(item) => (
                <span
                  className={`timeline-marker p-2 border-circle ${
                    item.changeType === "increment"
                      ? "bg-success"
                      : item.changeType === "decrement"
                        ? "bg-warning"
                        : item.changeType === "initial"
                          ? "bg-info"
                          : "bg-primary"
                  } text-white`}
                >
                  <i
                    className={`pi ${
                      item.changeType === "increment"
                        ? "pi-arrow-up"
                        : item.changeType === "decrement"
                          ? "pi-arrow-down"
                          : item.changeType === "initial"
                            ? "pi-plus-circle"
                            : "pi-pencil"
                    }`}
                  ></i>
                </span>
              )}
              content={timelineTemplate}
              opposite={(item) => (
                <small className="text-muted">
                  {new Date(item.timestamp).toLocaleDateString()}
                </small>
              )}
            />
          ) : (
            <div className="text-center py-5">
              <i
                className="pi pi-history text-muted"
                style={{ fontSize: "3rem" }}
              ></i>
              <h4 className="mt-3 text-muted">No History Available</h4>
              <p className="text-muted">
                No changes have been recorded for this stock item.
              </p>
            </div>
          )}
        </ScrollPanel>

        <Divider />

        <div className="d-flex justify-content-end pt-3">
          <Button
            type="button"
            label="Close"
            icon="pi pi-times"
            onClick={() => setShowHistoryModal(false)}
            className="p-button-outlined p-button-secondary"
          />
        </div>
      </Dialog>

      {/* Stock Requisition Modal */}
      <Dialog
        header={
          <div className="d-flex align-items-center">
            <i className="pi pi-send me-2"></i>
            Issue Stock
          </div>
        }
        visible={showRequisitionModal}
        onHide={() => setShowRequisitionModal(false)}
        style={{ width: "500px" }}
        breakpoints={{ "960px": "75vw", "641px": "95vw" }}
        modal
        className="p-fluid"
      >
        <div className="alert alert-warning d-flex align-items-center mb-3">
          <i className="pi pi-exclamation-triangle me-2"></i>
          <div>
            <small>Issuing stock from inventory</small>
            <br />
            <small className="text-muted">
              Available Stock: {requisitionData.currentQuantity} units
            </small>
          </div>
        </div>

        <form onSubmit={handleRequisitionSubmit}>
          <div className="row">
            <div className="col-12 mb-3">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-send me-1"></i>
                  Quantity to Issue
                </label>
                <InputNumber
                  value={requisitionData.issueQuantity}
                  onValueChange={(e) =>
                    setRequisitionData({
                      ...requisitionData,
                      issueQuantity: e.value || 0,
                    })
                  }
                  min={1}
                  max={requisitionData.currentQuantity}
                  placeholder="Enter quantity to issue"
                  required
                />
              </div>
            </div>

            <div className="col-12 mb-4">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-comment me-1"></i>
                  Purpose/Description
                </label>
                <InputTextarea
                  value={requisitionData.issueDescription}
                  onChange={(e) =>
                    setRequisitionData({
                      ...requisitionData,
                      issueDescription: e.target.value,
                    })
                  }
                  placeholder="Purpose of stock requisition"
                  rows={3}
                  required
                />
              </div>
            </div>
          </div>

          <Divider />

          <div className="d-flex justify-content-end gap-2 pt-3">
            <Button
              type="button"
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setShowRequisitionModal(false)}
              className="p-button-outlined p-button-secondary"
            />
            <Button
              type="submit"
              label="Issue Stock"
              icon="pi pi-check"
              loading={loading}
              className="p-button-primary"
            />
          </div>
        </form>
      </Dialog>
    </Layout>
  );
};

export default StockAndSpareManagement;
